import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Tooltip } from "antd";
import {
	History,
	CheckCircle2,
	AlertTriangle,
	Loader2,
	FileText,
	Calendar,
	PieChart,
	ChevronRight,
	ClipboardList,
	ArrowUpRight,
	CircleCheck,
	RotateCcw,
	TriangleAlert
} from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import PlanReviewPageHeader from "components/plan-review-page-header/plan-review-page-header";
import PlanReviewLoadError from "components/plan-review-load-error/plan-review-load-error";

import { useGetPlanReviewWithSubmissionsSubscription } from "graphql/subscriptions/plan-review-with-submissions.generated";
import { usePlanReviewFlowStore } from "state/zustand-stores/plan-review-flow-store";
import routeNames from "constants/route-names";
import { getPropertyAddress } from "utils/helpers";
import { PlanReviewJobStatus, PlanReviewReviewOutcome } from "constants/field-values";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildPlanReviewDescription } from "utils/ollie-screen-description";

type SubmissionStatus = "running" | "approved" | "revisions" | "failed";

const STATUS_META: Record<SubmissionStatus, { label: string; className: string; icon: typeof CheckCircle2 }> = {
	running: { label: "In Progress", className: "prs-status-running", icon: Loader2 },
	approved: { label: "Approved", className: "prs-status-approved", icon: CheckCircle2 },
	revisions: { label: "Revisions Needed", className: "prs-status-revisions", icon: AlertTriangle },
	// A failed run is the analysis breaking down, not a verdict on the package: its own
	// wording, so it is never read as a rejection.
	failed: { label: "Oops! Laid an Egg", className: "prs-status-failed", icon: TriangleAlert }
};

const deriveStatus = (state?: string | null, outcome?: string | null): SubmissionStatus => {
	if (state === PlanReviewJobStatus.FAILED) return "failed";
	if (state !== PlanReviewJobStatus.COMPLETE) return "running";
	if (outcome === PlanReviewReviewOutcome.APPROVED) return "approved";
	return "revisions";
};

type Job = NonNullable<
	NonNullable<ReturnType<typeof useGetPlanReviewWithSubmissionsSubscription>["data"]>["plan_reviews_by_pk"]
>["plan_review_jobs"][number];
const formatDate = (raw?: string | null): string => (raw ? dayjs(raw).format("MMMM D, YYYY") : "—");

/**
 * The submission history of one plan review project.
 *
 * A resubmission no longer overwrites the previous run, so this is where the applicant
 * picks which attempt to read. Submittals are listed newest first, each summarised by
 * the comments it is still waiting on.
 */
const PlanReviewSubmissions = () => {
	const navigate = useNavigate();
	const { planReviewId } = useParams<{ planReviewId: string }>();
	const setSelection = usePlanReviewFlowStore(state => state.setSelection);

	const { data, loading, error } = useGetPlanReviewWithSubmissionsSubscription({
		skip: !planReviewId,
		variables: { id: planReviewId as string },
		onData: data => {
			console.log("useGetPlanReviewWithSubmissionsSubscription data:", data);
		}
	});

	const planReview = data?.plan_reviews_by_pk;
	const submissions = useMemo(() => planReview?.plan_review_jobs ?? [], [planReview]);
	// Tells Ollie which project is on screen, so a question about "this submittal" has a subject.
	useDescribeScreenToOllie(
		buildPlanReviewDescription({
			planReviewId: planReview?.id,
			submissionTypeId: planReview?.submission_type?.id,
			propertyName: planReview?.property?.name,
			jurisdictionName: planReview?.jurisdiction_record?.name,
			submissionTypeName: planReview?.submission_type?.name,
			reviewType: planReview?.review_type,
			submissionJobIds: submissions.map(job => job.id),
			propertyId: planReview?.property_id,
			jurisdictionId: planReview?.jurisdiction_id,
			organizationId: planReview?.organization_id,
			workspaceId: planReview?.workspace_id,
			createdByUserId: planReview?.created_by_user_id
		}),
		{ propertyId: planReview?.property_id, jurisdictionId: planReview?.jurisdiction_id }
	);


	// A run already in flight would be rejected by the action, so the button waits for
	// it rather than sending the user to an upload screen that cannot submit.
	const hasRunningSubmission = submissions.some(
		job => job.state !== PlanReviewJobStatus.COMPLETE && job.state !== PlanReviewJobStatus.FAILED
	);

	const handleNewSubmission = useCallback(() => {
		if (!planReview) return;
		setSelection({
			organizationId: planReview.organization_id ?? "",
			propertyId: planReview.property_id ?? "",
			jurisdictionId: planReview.jurisdiction_id ?? "",
			submissionTypeId: planReview.submission_type?.id ?? "",
			propertyName: planReview.property?.name ?? ""
		});
		navigate(routeNames.PLAN_REVIEW_COLLECTION_RESUBMIT.replace(":planReviewId", planReview.id));
	}, [planReview, setSelection, navigate]);

	if (loading && !planReview) return <LoadingOverlay />;

	// The subscription broke before it delivered anything: the project is not missing,
	// we just never got to read it, and a reload is what gets the user in.
	if (error && !planReview) return <PlanReviewLoadError />;

	if (!planReview) return <PlanReviewLoadError notFound />;

	const latestJob = submissions[0];
	const latestStatus = latestJob ? deriveStatus(latestJob.state, latestJob.review_outcome) : null;

	return (
		<OllieLayout>
			<PlanReviewPageHeader
				propertyName={planReview.property?.name}
				address={getPropertyAddress(planReview.property?.address, planReview.property?.standardized_address)}
				status={latestStatus ? { label: STATUS_META[latestStatus].label, tone: latestStatus } : null}
				submissionType={planReview.submission_type}
				ahjName={planReview.jurisdiction_record?.name}
				ollieId={planReview.ollie_id}
			>
				<button className="prph-btn-primary" onClick={handleNewSubmission} disabled={hasRunningSubmission}>
					<RotateCcw size={16} />
					{hasRunningSubmission ? "Analysis in progress…" : "Submit Revisions"}
				</button>
			</PlanReviewPageHeader>

			<div className="psf-page">
				<div className="prs-shell">
					<h2 className="prs-section-title">
						<History size={20} className="prs-section-icon" />
						Submittal History
					</h2>

					<div className="prs-list">
						{submissions.map((job, index) => {
							const status = deriveStatus(job.state, job.review_outcome);
							const meta = STATUS_META[status];
							const StatusIcon = meta.icon;
							const isComplete = job.state === PlanReviewJobStatus.COMPLETE;
							const isCurrent = index === 0;
							// Each submittal counts what its own comment letter lists, whatever later
							// submittals did with those comments.
							const commentCount = job.plan_review_findings.length;
							const score = job.compliance_score ?? 0;
							const documentCount = job.plan_review_documents_aggregate.aggregate?.count ?? 0;

							return (
								<div
									key={job.id}
									className={`prs-card${isCurrent ? " prs-card-current" : ""}`}
									role="button"
									tabIndex={0}
									onClick={() =>
										navigate(
											routeNames.PLAN_REVIEW_DETAILS.replace(":planReviewId", planReview.id).replace(":jobId", job.id)
										)
									}
									onKeyDown={event => {
										if (event.key !== "Enter" && event.key !== " ") return;
										event.preventDefault();
										navigate(
											routeNames.PLAN_REVIEW_DETAILS.replace(":planReviewId", planReview.id).replace(":jobId", job.id)
										);
									}}
								>
									<div className="prs-card-main">
										<div className="prs-card-head">
											<div>
												<div className="prs-card-titlerow">
													<h3 className="prs-card-title">
														Submittal #{job.submission_number} -{" "}
														{job.submission_number === 1 ? "Initial Plan Review" : "Resubmittal"}
													</h3>
													{isCurrent && <span className="prs-current-tag">Current</span>}
												</div>
												<div className="prs-card-meta">
													<span className="prs-card-meta-item">
														<Calendar size={16} />
														{formatDate(job.created_at)}
													</span>
													<span className="prs-card-meta-item">
														<FileText size={16} color={"#615fff"}/>
														{documentCount} {documentCount === 1 ? "Document" : "Documents"}
													</span>
													{isComplete && (
														<span className={`prs-card-meta-item ${score >= 100 ? "prs-complete" : "prs-in-review"}`}>
															<PieChart size={16} />
															{score}% Complete
														</span>
													)}
												</div>
											</div>

											<span className={`prs-status ${meta.className}`}>
												<StatusIcon size={16} />
												{meta.label}
											</span>
										</div>

										<p className="prs-card-summary">
											{job.state === PlanReviewJobStatus.FAILED
												? (job.error_message ?? "The review couldn't be completed.")
												: (job.summary ?? "Analysis in progress…")}
										</p>

										{isComplete && (
											<div className="prs-comments">
												<div className="prs-comments-head">
													<h4 className="prs-comments-title">
														<ClipboardList size={16} />
														Plan Review Comments
														<Tooltip title="See details">
															<button
																type="button"
																className="prs-comments-open"
																aria-label="Open the plan review comments for this submittal"
																onClick={event => {
																	event.stopPropagation();
																	navigate(
																		routeNames.PLAN_REVIEW_COMMENTS.replace(":planReviewId", planReview.id).replace(
																			":jobId",
																			job.id
																		)
																	);
																}}
															>
																<ArrowUpRight size={14} />
															</button>
														</Tooltip>
													</h4>

													{commentCount > 0 ? (
														<span className="prs-comments-count prs-comments-count-open">
															<AlertTriangle size={14} />
															Open: {commentCount}
														</span>
													) : (
														<span className="prs-comments-count prs-comments-count-addressed">
															<CircleCheck size={14} />
															No open items
														</span>
													)}
												</div>

												{commentCount > 0 ? (
													// Titles are not part of the free tier (MWA-788) — a count is, and the
													// full comment letter is one click away for whoever has paid.
													<div className="prs-comments-body">
														<p className="prs-comments-label">
															{commentCount} {commentCount === 1 ? "item needs" : "items need"} your attention.
															Open the comments to see what to fix.
														</p>
													</div>
												) : (
													<div className="prs-comments-body prs-comments-clear">
														<CircleCheck size={16} />
														<span>This submittal did not raise any revisions.</span>
													</div>
												)}
											</div>
										)}
									</div>

									<div className="prs-card-side">
										<button
											type="button"
											className="prs-card-open"
											aria-label={`Open the report of submittal #${job.submission_number}`}
											onClick={event => {
												event.stopPropagation();
												navigate(
													routeNames.PLAN_REVIEW_DETAILS.replace(":planReviewId", planReview.id).replace(
														":jobId",
														job.id
													)
												);
											}}
										>
											<ChevronRight size={24} />
										</button>
									</div>
								</div>
							);
						})}

						{submissions.length === 0 && (
							<div className="prs-empty">
								<p>This plan review has no submissions yet.</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</OllieLayout>
	);
};

export default PlanReviewSubmissions;
